import React from "react";
import SendAPBImageList from "../../../components/sendAPBImageList/sendAPBImageList";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { EASInvestigationFieldsErrorMessage, MAX_LENGTH_SUMMARY_TEXT, XL_GRID_PROPS } from "../../../constants";
import { IDSInvestigation , EDSInvestigationFields} from "../../../models/dsException";

interface Day15InvestigationProps {
  values: any;
  osdNumber: number;
  exceptionType?: string;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  selectedImages: string[];
  setSelectedImages: (image: string[]) => void;
  setIsShowSendAPB: (show: boolean) => void;
  formFieldsDisabled?: boolean;
  isControlDisabled: (fieldName: any) => boolean | undefined;
  getErrorMessage: (fieldName: any) => React.ReactNode;
  isValueExist: (
    fieldName: string,
    index?: number,
    fieldArrayName?: string
  ) => void;
  handleChange: (event: any) => void;
  handleBlur: (event: React.FocusEvent<any, Element>) => void;
  renderF18Note: (fieldName: any) => JSX.Element;
  renderCompletedError: (
    fieldName: string,
    day: keyof typeof EASInvestigationFieldsErrorMessage,
    conditionalFlag?: boolean
  ) => JSX.Element | null;
  hasError: (fieldName: any) => boolean;
  initValues?: IDSInvestigation;
  activeStep?: number;
}

const Day15Investigation: React.FC<Day15InvestigationProps> = ({
  values,
  setFieldValue,
  osdNumber,
  exceptionType,
  isControlDisabled,
  formFieldsDisabled = false,
  getErrorMessage,
  isValueExist,
  handleChange,
  handleBlur,
  renderF18Note,
  renderCompletedError,
  hasError,
  initValues = {},
  activeStep,
  selectedImages,
  setSelectedImages,
  setIsShowSendAPB,
}) => {
  // Handle image selection
  const handleImageSelect = (imageUrl: string) => {
    if (selectedImages.includes(imageUrl)) {
      setSelectedImages(
        selectedImages.filter((url: string) => url !== imageUrl)
      );
    } else {
      setSelectedImages([...selectedImages, imageUrl]);
    }
  };

  /**
   * Determines whether the "Send APB" option should be displayed based on the current state and form values.
   * .
   */
  const showSendAPB = () => {
    const { highValue, details7818_2, check7818_2 } = values;
    const {
      highValue: initialHighValue,
      details7818_2: initialDetails7818_2,
      check7818_2: initialCheck7818_2,
    } = initValues;

    const hasChangedValue =
      initialHighValue !== "Y" ||
      initialCheck7818_2 !== "Y" ||
      !initialDetails7818_2?.trim();

    if (
      !formFieldsDisabled &&
      highValue === "Y" &&
      details7818_2?.trim() &&
      check7818_2 === "Y" &&
      hasChangedValue
    ) {
      setIsShowSendAPB(true);
      return true;
    } else {
      setIsShowSendAPB(false);
      return false;
    }
  };

  // Removed useEffect to prevent infinite update loop

  return (
    <div>
      <Grid container spacing={2} className="filter-margin">
        <Grid item {...XL_GRID_PROPS}>
          <FormControl className="radio-group question-group ">
            <FormLabel>
              <FormControlLabel
                className="checkbox-shoartage"
                disabled={
                  isControlDisabled("check7818_2") ?? formFieldsDisabled
                }
                control={
                  <Checkbox
                    disableRipple
                    checked={values.check7818_2 === "Y"}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue(
                        "check7818_2",
                        e.target.checked ? "Y" : "N"
                      );
                      setFieldValue(
                        "details7818_2",
                        e.target.checked ? values.details7818_2 : "",
                        true
                      );
                    }}
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                  />
                }
                label=""
              />{" "}
              Check 78/18 and/or Power BI for any overages (OV, NB, DO) to apply{" "}
              <sup>*</sup>
            </FormLabel>
            <Grid container spacing={2}>
              <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                {values.check7818_2 === "Y" && (
                  <TextField
                    autoComplete="off"
                    name="details7818_2"
                    value={values.details7818_2 ?? ""}
                    onChange={handleChange}
                    onBlur={(e) => {
                      handleBlur(e);
                      isValueExist(EDSInvestigationFields.Details7818_2);
                    }}
                    disabled={formFieldsDisabled}
                    fullWidth
                    placeholder="Provide details of your search"
                    error={hasError("details7818_2")}
                    inputProps={{
                      maxLength: MAX_LENGTH_SUMMARY_TEXT,
                    }}
                    variant="outlined"
                  />
                )}
                {getErrorMessage("check7818_2")}
                {getErrorMessage("details7818_2")}
                {renderCompletedError("details7818_2", "day15")}
              </Grid>
            </Grid>
          </FormControl>
        </Grid>
        <Grid item {...XL_GRID_PROPS}>
          <FormControl className="radio-group question-group ">
            <FormLabel id="destinationTerminal">
              Is this high value or high quantity? <sup>*</sup>
            </FormLabel>
            <Grid container spacing={2}>
              <Grid item {...XL_GRID_PROPS} className="all-short-entry">
                <RadioGroup
                  row
                  aria-labelledby="destinationTerminal"
                  name="highValue"
                  className="textarea-radio-group"
                >
                  <FormControlLabel
                    value="Y"
                    control={<Radio />}
                    label="Yes"
                    name="highValue"
                    checked={values.highValue === "Y"}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={formFieldsDisabled}
                  />
                  <FormControlLabel
                    value="N"
                    control={<Radio />}
                    label="No"
                    name="highValue"
                    checked={values.highValue === "N"}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={formFieldsDisabled}
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          </FormControl>
          {getErrorMessage("highValue")}
        </Grid>
      </Grid>
      {/* {showSendAPB() && ( */}
      <div className={`${showSendAPB() ? "" : "hide-me"} another-class-name`}>
        <div className="MuiTypography-root header-notice justify-content">
          Since you've chosen the "high value/quantity" option, an APB will be
          issued. Please remove any photos you'd prefer not to include in the
          APB email.
        </div>
        <SendAPBImageList
          osdNumber={osdNumber}
          setSelectedImages={setSelectedImages}
          selectedImages={selectedImages}
          handleImageSelect={handleImageSelect}
          exceptionType={exceptionType}
          isSendAPBScreen={false}
        />
        <br />
      </div>
      {/* )} */}
      <Grid container spacing={2} className="filter-margin">
        {renderF18Note("day15F18Note")}
      </Grid>
    </div>
  );
};

export default Day15Investigation;
